/* eslint-disable */
// Pure (no-React) helpers for the Delay Report export.
//
// Delay definition (David's spec — distinct from the bell's STATUS_DAYS):
//   time-in-stage = floor((now - stageStartedAt) / day), fallback chain
//   stageStartedAt → data.stageStartedAt → data.created_at → created_at
//   (mirrors getDaysInStage in src/utils/devCardUtils.js). A dev is "delayed"
//   when time-in-stage > the per-stage threshold (defaults 14/30/45).
//
// Inclusion: working stage only (Drawing/Hand/Color), stage enabled in the
// dialog, NOT archived, and a classifiable side (getStatusSide → factory or
// designer). Factory mode further restricts to side === 'factory'.

import { STAGES } from '../../../shared';
import { devIsMovedToOrder } from '../../../utils/devLock';
import { getStatusSide } from '../../../constants/statusSide';
import { extractFactoryCode } from '../../../utils/factoryMatch';
import { displayDesigner } from '../analytics/sections/people/designerDisplay';

const MS_PER_DAY = 86400000;

// stage_status → dialog threshold key
export const STAGE_KEY = {
  [STAGES.DRAWING]: 'drawing',
  [STAGES.HAND]:    'hand',
  [STAGES.COLOR]:   'color',
};

export const DEFAULT_THRESHOLDS = { drawing: 14, hand: 30, color: 45 };

// Defensive merge — dev rows arrive flattened (PostgREST projection) but tolerate
// nested data too (Convention #58 / G.1 pattern).
function field(dev, key) {
  return dev?.[key] ?? dev?.data?.[key];
}

// Days the CURRENT status/request has been waiting — clocks from status_set_at
// (when the current status was set, i.e. how long the ball has sat in the
// current court), NOT stageStartedAt (total time in the whole stage, which
// over-counts devs that have bounced through revisions). Fallback chain
// degrades gracefully for legacy rows lacking status_set_at.
export function daysWaiting(dev) {
  const startedAt =
    dev?.status_set_at ?? dev?.data?.status_set_at ??
    dev?.stageStartedAt ?? dev?.data?.stageStartedAt ??
    dev?.data?.created_at ?? dev?.created_at;
  if (!startedAt) return null;
  const ms = new Date(startedAt).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor((Date.now() - ms) / MS_PER_DAY));
}

function devImageUrl(dev) {
  const imgs = (dev?.images?.length ? dev.images : dev?.data?.images) || [];
  if (!imgs.length) return null;
  const pidx = dev?.data?.primaryImageIndex ?? dev?.primaryImageIndex ?? 0;
  const img = (pidx > 0 && pidx < imgs.length) ? imgs[pidx] : imgs[0];
  return typeof img === 'string' ? img : (img?.url || null);
}

/**
 * Build the delayed-dev rows for a mode, BEFORE scope (group) filtering.
 *
 * @param {Array}  devs
 * @param {Object} opts
 * @param {Object} opts.thresholds  { drawing, hand, color } (days)
 * @param {Object} opts.stagesOn    { drawing, hand, color } (booleans)
 * @param {String} opts.mode        'des' | 'fty'
 * @returns {Array<Row>}  Row = { devId, devNumber, model, designer, factoryCode,
 *   factoryFull, groupKey, groupLabel, stage, stageKey, status, side,
 *   daysWaiting, threshold, daysOver, statusSetAt, imageUrl }
 */
export function computeDelayRows(devs, { thresholds, stagesOn, mode }) {
  const rows = [];
  for (const dev of devs || []) {
    if (dev?.archived || dev?.data?.archived) continue;
    if (devIsMovedToOrder(dev)) continue;     // Collection C2 — locked: nobody is waiting on it

    const stage = field(dev, 'stage_status');
    const stageKey = STAGE_KEY[stage];
    if (!stageKey) continue;                 // not a working stage (Done/unknown)
    if (stagesOn && stagesOn[stageKey] === false) continue;

    const status = field(dev, 'full_status');
    const side = getStatusSide(stage, status);
    if (side !== 'factory' && side !== 'designer') continue;  // neither/unknown → exclude
    if (mode === 'fty' && side !== 'factory') continue;        // chase-list: factory-side only

    const threshold = Number(thresholds?.[stageKey]);
    if (!Number.isFinite(threshold)) continue;                 // stage off / empty threshold → skip
    const waiting = daysWaiting(dev);
    if (waiting == null || waiting <= threshold) continue;     // not delayed (current status within range)

    const designerRaw = field(dev, 'designer') || '';
    const designerLabel = displayDesigner(designerRaw);
    const ftyRaw = field(dev, 'fty') || '';
    const factoryCode = extractFactoryCode(ftyRaw);

    // Group key/label per mode. Skip rows with no group anchor.
    let groupKey, groupLabel;
    if (mode === 'des') {
      if (!designerLabel) continue;
      groupKey = designerRaw.trim().toLowerCase();
      groupLabel = designerLabel;
    } else {
      if (!factoryCode) continue;
      groupKey = factoryCode;
      groupLabel = ftyRaw || factoryCode;
    }

    rows.push({
      devId: dev.id,
      devNumber: field(dev, 'dev_number') || '',
      model: field(dev, 'model') || '',
      designer: designerLabel || '',
      factoryCode,
      factoryFull: ftyRaw,
      groupKey,
      groupLabel,
      stage,
      stageKey,
      status: status || '',
      side,
      daysWaiting: waiting,
      threshold,
      daysOver: waiting - threshold,
      statusSetAt: field(dev, 'status_set_at') || '',
      imageUrl: devImageUrl(dev),
    });
  }
  return rows;
}

/** Distinct {key,label} groups from rows, sorted by label (case-insensitive). */
export function groupsFromRows(rows) {
  const byKey = new Map();
  for (const r of rows) if (!byKey.has(r.groupKey)) byKey.set(r.groupKey, r.groupLabel);
  return Array.from(byKey, ([key, label]) => ({ key, label }))
    .sort((a, b) => String(a.label).toLowerCase().localeCompare(String(b.label).toLowerCase()));
}

/** Excel-safe sheet name: strip : \ / ? * [ ], collapse spaces, ≤31 chars. */
export function sanitizeSheetName(name, fallback = 'Sheet') {
  let s = String(name || '').replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) s = fallback;
  return s.slice(0, 31);
}

/** DD/MM/YYYY from an ISO string; '' when missing/unparseable. */
export function formatDMY(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** YYYY-MM-DD (local) for filenames. */
export function todayStamp() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
